import React from 'react';
import { Card, Button, Row, Col, Typography, Steps, Tag } from 'antd';
import { useHistory } from 'react-router-dom';
import { ArrowLeftOutlined, HeartOutlined, StarOutlined, CrownOutlined, RocketOutlined } from '@ant-design/icons';
import calmiHeartImage from '../../assets/images/Calmi Heart.png';

const { Title, Text, Paragraph } = Typography;
const { Step } = Steps;

const CalmiHeartBrands = () => {
  const history = useHistory();

  const brandFeatures = [
    {
      title: 'Complete White-Label Solution',
      description: 'Your brand, your voice, your market - powered by our proven AI technology.',
      icon: '🏷️',
      benefits: ['Custom branding throughout app and voice responses', 'Your brand voice and personality', 'Custom packaging and marketing materials', 'Dedicated brand domain and app store presence']
    },
    {
      title: 'Market Differentiation',
      description: 'Stand out in competitive markets with unique AI-powered emotional wellness features.',
      icon: '🚀',
      benefits: ['First-mover advantage in AI plush toys', 'Emotional wellness positioning', 'Premium price justification', 'Media attention and PR opportunities']
    },
    {
      title: 'Customer Lifetime Value',
      description: 'Build deeper relationships with customers through ongoing AI interactions and insights.',
      icon: '💝',
      benefits: ['Ongoing customer engagement through AI', 'Parent dashboard creates stickiness', 'Data insights for product development', 'Community building opportunities']
    }
  ];

  const customizationOptions = [
    {
      category: 'AI Personality',
      options: ['Custom voice personality and tone', 'Branded character development', 'Unique conversation topics and interests', 'Cultural and language adaptations'],
      icon: '🎭'
    },
    {
      category: 'Visual Brand Identity',
      options: ['Custom app interface design', 'Branded packaging and materials', 'Logo integration throughout experience', 'Color scheme and visual consistency'],
      icon: '🎨'
    },
    {
      category: 'Market Positioning',
      options: ['Target audience customization', 'Age-appropriate content tuning', 'Educational vs entertainment focus', 'Therapeutic vs play positioning'],
      icon: '🎯'
    },
    {
      category: 'Business Model',
      options: ['One-time purchase model', 'Subscription service options', 'Premium feature tiers', 'Enterprise/institutional sales'],
      icon: '💼'
    }
  ];

  const successStories = [
    {
      brand: 'Wellness Bear Co.',
      category: 'Therapeutic Toys',
      challenge: 'Needed to differentiate premium plush toys in crowded market',
      solution: 'Integrated Calmi Heart with therapeutic conversation focus',
      results: ['300% price premium vs traditional plush', '95% customer satisfaction', '60% repeat purchase rate'],
      color: '#1890ff'
    },
    {
      brand: 'Little Friends Inc.',
      category: 'Educational Toys',
      challenge: 'Parents wanted screen-free learning experiences',
      solution: 'Customized AI for educational conversations and skill building',
      results: ['Featured in top education blogs', '40% month-over-month growth', 'Expanded to 5 international markets'],
      color: '#52c41a'
    },
    {
      brand: 'Comfort Companions',
      category: 'Special Needs',
      challenge: 'Children with autism needed consistent emotional support',
      solution: 'Specialized AI training for autism spectrum support',
      results: ['Endorsed by autism organizations', 'Therapy center partnerships', '90% therapist recommendation rate'],
      color: '#722ed1'
    }
  ];

  const launchSteps = [
    {
      title: 'Brand Discovery',
      description: 'We work with your team to understand your brand values, target audience, and market positioning.'
    },
    {
      title: 'AI Customization',
      description: 'Our AI team customizes the personality, conversation style, and focus areas to match your brand.'
    },
    {
      title: 'Design Integration',
      description: 'Your visual brand identity is integrated throughout the app, packaging, and user experience.'
    },
    {
      title: 'Testing & Refinement',
      description: 'Comprehensive testing with your target audience to ensure brand alignment and effectiveness.'
    },
    {
      title: 'Market Launch',
      description: 'Go-to-market support including PR, marketing materials, and launch strategy guidance.'
    }
  ];

  return (
    <div style={{ minHeight: '100vh', background: '#f0f2f5' }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #fd746c 0%, #ff9068 100%)',
        padding: '60px 0',
        marginBottom: '40px'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
          <Button 
            type="text" 
            icon={<ArrowLeftOutlined />} 
            onClick={() => history.push('/heart')}
            style={{ color: 'white', marginBottom: '20px' }}
          >
            Back to Calmi Heart
          </Button>
          
          <Row align="middle" gutter={[32, 32]}>
            <Col xs={24} md={16}>
              <img 
                src={calmiHeartImage} 
                alt="Calmi Heart"
                style={{ 
                  height: '80px',
                  objectFit: 'contain',
                  marginBottom: '16px'
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.parentNode.innerHTML = '<div style="font-size: 48px; color: white; margin-bottom: 16px;">🏆</div>';
                }}
              />
              <Title level={1} style={{ color: 'white', marginBottom: '16px' }}>
                For Brands & Retailers
              </Title>
              <Paragraph style={{ color: 'rgba(255,255,255,0.9)', fontSize: '20px', marginBottom: '0' }}>
                Transform your plush toy line with AI-powered emotional intelligence. 
                White-label solutions that amplify your brand and create deeper customer connections.
              </Paragraph>
            </Col>
            <Col xs={24} md={8}>
              <div style={{
                background: 'rgba(255,255,255,0.1)',
                borderRadius: '12px',
                padding: '24px',
                textAlign: 'center',
                backdropFilter: 'blur(10px)'
              }}>
                <div style={{ fontSize: '32px', marginBottom: '8px' }}>🏆</div>
                <Text style={{ color: 'white', fontSize: '16px', fontWeight: 'bold' }}>
                  Brand Excellence
                </Text>
              </div>
            </Col>
          </Row>
        </div>
      </div>

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
        
        {/* Key Benefits */}
        <div style={{ marginBottom: '60px' }}>
          <Title level={2} style={{ textAlign: 'center', marginBottom: '40px' }}>
            Why Brands Choose Calmi Heart
          </Title>
          
          <Row gutter={[32, 32]}>
            {brandFeatures.map((feature, index) => (
              <Col xs={24} lg={8} key={index}>
                <Card style={{ height: '100%', borderRadius: '12px', border: 'none', boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}>
                  <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>{feature.icon}</div>
                    <Title level={3} style={{ marginBottom: '8px' }}>{feature.title}</Title>
                    <Text style={{ fontSize: '16px', color: '#666' }}>{feature.description}</Text>
                  </div>
                  
                  <div>
                    {feature.benefits.map((benefit, idx) => (
                      <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '8px' }}>
                        <StarOutlined style={{ fontSize: '12px', color: '#52c41a', marginTop: '4px' }} />
                        <Text style={{ fontSize: '14px' }}>{benefit}</Text>
                      </div>
                    ))}
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
        </div>

        {/* Customization Options */}
        <Card style={{ marginBottom: '60px', borderRadius: '12px' }}>
          <Title level={2} style={{ marginBottom: '24px', textAlign: 'center' }}>
            <CrownOutlined style={{ marginRight: '12px', color: '#722ed1' }} />
            Complete Customization Options
          </Title>
          <Paragraph style={{ fontSize: '16px', color: '#666', textAlign: 'center', marginBottom: '40px' }}>
            Every aspect of the AI companion experience can be customized to perfectly match your brand identity and market positioning.
          </Paragraph>
          
          <Row gutter={[24, 24]}>
            {customizationOptions.map((option, index) => (
              <Col xs={24} md={12} key={index}>
                <div style={{ 
                  border: '1px solid #f0f0f0',
                  borderRadius: '8px',
                  padding: '24px',
                  height: '100%'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
                    <span style={{ fontSize: '24px', marginRight: '12px' }}>{option.icon}</span>
                    <Title level={4} style={{ margin: 0 }}>{option.category}</Title>
                  </div>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {option.options.map((opt, idx) => (
                      <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ 
                          width: '6px', 
                          height: '6px', 
                          borderRadius: '50%', 
                          background: '#722ed1' 
                        }} />
                        <Text style={{ fontSize: '14px' }}>{opt}</Text>
                      </div>
                    ))}
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </Card>

        {/* Success Stories */}
        <div style={{ marginBottom: '60px' }}>
          <Title level={2} style={{ textAlign: 'center', marginBottom: '40px' }}>
            Brand Success Stories
          </Title>
          
          <Row gutter={[24, 24]}>
            {successStories.map((story, index) => (
              <Col xs={24} lg={8} key={index}>
                <Card style={{ 
                  height: '100%', 
                  borderRadius: '12px',
                  border: `2px solid ${story.color}`,
                  boxShadow: '0 4px 16px rgba(0,0,0,0.08)'
                }}>
                  <div style={{ marginBottom: '16px' }}>
                    <Title level={4} style={{ color: story.color, marginBottom: '4px' }}>
                      {story.brand}
                    </Title>
                    <Tag color={story.color}>{story.category}</Tag>
                  </div>
                  
                  <div style={{ marginBottom: '16px' }}>
                    <Text strong style={{ fontSize: '14px', display: 'block', marginBottom: '4px' }}>Challenge:</Text>
                    <Text style={{ fontSize: '14px', color: '#666', display: 'block', marginBottom: '12px' }}>{story.challenge}</Text>
                    
                    <Text strong style={{ fontSize: '14px', display: 'block', marginBottom: '4px' }}>Solution:</Text>
                    <Text style={{ fontSize: '14px', color: '#666', display: 'block', marginBottom: '12px' }}>{story.solution}</Text>
                  </div>
                  
                  <div>
                    <Text strong style={{ fontSize: '14px', display: 'block', marginBottom: '8px' }}>Results:</Text>
                    {story.results.map((result, idx) => (
                      <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                        <HeartOutlined style={{ fontSize: '12px', color: story.color }} />
                        <Text style={{ fontSize: '13px' }}>{result}</Text>
                      </div>
                    ))}
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
        </div>

        {/* Launch Process */}
        <Card style={{ marginBottom: '60px', borderRadius: '12px' }}>
          <Title level={2} style={{ marginBottom: '32px', textAlign: 'center' }}>
            <RocketOutlined style={{ marginRight: '12px', color: '#1890ff' }} />
            From Concept to Market in 90 Days
          </Title>
          
          <Steps 
            direction="vertical" 
            current={-1}
            style={{ maxWidth: '800px', margin: '0 auto' }}
          >
            {launchSteps.map((step, index) => (
              <Step
                key={index}
                title={<span style={{ fontSize: '16px', fontWeight: 'bold' }}>{step.title}</span>}
                description={<span style={{ fontSize: '14px', color: '#666' }}>{step.description}</span>}
                icon={<div style={{ 
                  background: '#1890ff', 
                  color: 'white', 
                  width: '24px', 
                  height: '24px', 
                  borderRadius: '50%', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  fontSize: '12px',
                  fontWeight: 'bold'
                }}>{index + 1}</div>}
              />
            ))}
          </Steps>
        </Card>

        {/* Pricing Tiers */}
        <Card style={{ marginBottom: '60px', borderRadius: '12px' }}>
          <Title level={2} style={{ marginBottom: '32px', textAlign: 'center' }}>
            Partnership Tiers
          </Title>
          
          <Row gutter={[24, 24]}>
            <Col xs={24} md={8}>
              <div style={{
                border: '2px solid #d9d9d9',
                borderRadius: '12px',
                padding: '32px',
                textAlign: 'center',
                height: '100%'
              }}>
                <Title level={3} style={{ color: '#666' }}>Starter</Title>
                <div style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '16px' }}>
                  Basic Customization
                </div>
                <ul style={{ textAlign: 'left', fontSize: '14px' }}>
                  <li>Basic brand integration</li>
                  <li>Standard AI personality</li>
                  <li>1,000 unit minimum</li>
                  <li>Email support</li>
                </ul>
              </div>
            </Col>
            
            <Col xs={24} md={8}>
              <div style={{
                border: '2px solid #1890ff',
                borderRadius: '12px',
                padding: '32px',
                textAlign: 'center',
                height: '100%',
                position: 'relative'
              }}>
                <div style={{
                  position: 'absolute',
                  top: '-12px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: '#1890ff',
                  color: 'white',
                  padding: '4px 16px',
                  borderRadius: '20px',
                  fontSize: '12px',
                  fontWeight: 'bold'
                }}>
                  POPULAR
                </div>
                <Title level={3} style={{ color: '#1890ff' }}>Professional</Title>
                <div style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '16px' }}>
                  Full Customization
                </div>
                <ul style={{ textAlign: 'left', fontSize: '14px' }}>
                  <li>Complete brand customization</li>
                  <li>Custom AI personality</li>
                  <li>500 unit minimum</li>
                  <li>Dedicated support team</li>
                  <li>Marketing support</li>
                </ul>
              </div>
            </Col>
            
            <Col xs={24} md={8}>
              <div style={{
                border: '2px solid #52c41a',
                borderRadius: '12px',
                padding: '32px',
                textAlign: 'center',
                height: '100%'
              }}>
                <Title level={3} style={{ color: '#52c41a' }}>Enterprise</Title>
                <div style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '16px' }}>
                  White-Label Platform
                </div>
                <ul style={{ textAlign: 'left', fontSize: '14px' }}>
                  <li>Complete platform ownership</li>
                  <li>Advanced AI customization</li>
                  <li>No minimum order</li>
                  <li>24/7 technical support</li>
                  <li>Co-marketing opportunities</li>
                </ul>
              </div>
            </Col>
          </Row>
        </Card>

        {/* CTA */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '40px',
          textAlign: 'center',
          boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
          marginBottom: '60px'
        }}>
          <Title level={3}>Ready to Transform Your Brand?</Title>
          <Paragraph style={{ fontSize: '16px', color: '#666', marginBottom: '24px' }}>
            Let's discuss how Calmi Heart can elevate your brand and create meaningful 
            connections with your customers.
          </Paragraph>
          <Button
            type="primary"
            size="large"
            style={{
              background: '#fd746c',
              borderColor: '#fd746c',
              height: '48px',
              fontSize: '16px',
              fontWeight: 'bold',
              padding: '0 32px',
              borderRadius: '24px',
              marginRight: '16px'
            }}
          >
            Schedule Brand Consultation
          </Button>
          <Button
            size="large"
            onClick={() => history.push('/heart/technical')}
            style={{
              height: '48px',
              fontSize: '16px',
              padding: '0 32px',
              borderRadius: '24px'
            }}
          >
            View Technical Details
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CalmiHeartBrands;